import React, { useEffect, useRef, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import { useDispatch, useSelector } from 'react-redux';
import { useGridApiRef, gridFilteredSortedRowEntriesSelector } from '@mui/x-data-grid-pro';
import pinGreen from '../../assets/images/pin_green.png';
import pinRed from '../../assets/images/pin_red.png';
import pinAmber from '../../assets/images/pin_amber.png';
import { Icon } from 'leaflet';
import Datagrid from '../ui/Datagrid';
import columns from './table_config';
import { Box, CircularProgress, Grid, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { createNewWell, deleteWell, downloadWellInputs, listWells, updateWellDetails, updateWells, updateWellsFavorite } from '../../store/actions/wells';
import { SaveRounded, DeleteRounded, DirectionsRunRounded, Star, StarBorder, Handshake, FileUpload, CloudSyncRounded, CalendarViewDay, DragHandle, Water, StackedLineChart, Sensors, FactCheckTwoTone, FileDownload, PersonAddAlt } from '@mui/icons-material';
import { MAP_TYPES, REDUX_ACTIONS } from '../../shared/config/constants';
import SurveyModal from '../survey';
import Map from '../map';
import { listRigs } from '../../store/actions/rigs';
import SelectAutoComplete from '../ui/SelectAutoCompleteCell';
import GeometryModal from '../geometry';
import store from '../../store/store';
import DrillStringModal from '../drillString';
import DrillFluidModal from '../drillFluid';
import { enqueueSnackbar } from '../../store/actions/snackbar';
import SelectEditInputTextField from '../ui/SelectEditInputTextField';
import HydraulicsModal from '../ui/HydraulicsModalDialog';
import DeleteConfirmationModal from '../deleteConfirmationModal';
import WindowModal from '../window';
import { PERMISSION_DIALOG_SOURCES, TOOLTIPS, USER_TYPE } from '../../utils/constants';
import WellsImportConfirmationModal from '../wellsImportConfirmationDialog';
import { listMPDCustomers } from '../../store/actions/customers';
import CustomerDialog from '../ui/CustomerDialog';
import { containsAnyKeys } from '../../utils/objectHelpers';
import PWDModal from '../pwd';
import WellsImportDialog from '../wellsImportDialog';
import styles, { listItemTextPrimary } from './styles';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import PermissionsModal from '../permissions';
import { checkHydraulicsAdmin } from '../../utils/checkPermissions';

const pins = {
  default: pinRed,
  green: pinGreen,
  red: pinRed,
  amber: pinAmber,
};
// adding static well data temporarily for page content - remove once backend flow for wells is ready
const Wells = () => {
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const classes = styles();
  const wells = useSelector((state) => state.wells);
  const mpdCustomer = useSelector((state) => state.mpdCustomer);
  const rigs = useSelector((state) => state.rigs);
  const user = useSelector((state) => state.currentUser);
  const mapRef = useRef(null);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [surveyData, setSurveyData] = useState();
  const mapType = process.env.REACT_APP_WELLS_PAGE_MAP;
  const [gmapRowId, setGmapRowId] = useState(null);
  const [rowId, setRowId] = useState(null);
  const [showGeometryModal, setShowGeometryModal] = useState(false);
  const [geometryData, setGeometryData] = useState();
  const [wellsData, setWellsData] = useState([]);
  const [showDrillStringModal, setShowDrillStringModal] = useState(false);
  const [drillStringData, setDrillStringData] = useState();
  const [showDrillFluidModal, setShowDrillFluidModal] = useState(false);
  const [drillFluidData, setDrillFluidData] = useState();
  const [showWindowModal, setShowWindowModal] = useState(false);
  const [windowData, setWindowData] = useState();
  const [rigOption, setRigOption] = useState([]);
  const [isHydraulicsDialogOpen, setIsHydraulicsDialogOpen] = useState(false);
  const [currentJob, setCurrentJob] = useState({});
  const [wellDetails, setWellDetails] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const [importConfirmAction, setImportConfirmAction] = useState(false);
  const [importRow, setImportRow] = useState();
  const [showMPDCustomer, setShowMPDCustomer] = useState({
    open: false,
    data: null
  });
  const [showPWDModal, setShowPWDModal] = useState({
    open: false,
    data: null
  });
  const [showImportDialog, setShowImportDialog] = useState({
    open: false,
    data: null
  });
  const [wellsInputsDownload, setWellsInputsDownload] = useState(false);
  const [permissions, setPermissions] = useState({
    open: false,
    data: {}
  });
  const isHydraulicsAdmin = checkHydraulicsAdmin(
    user?.type,
    user?.accessLevels
  );

  useEffect(() => {
    // this effect is used to update the map size when the user changes viewport to widescreen
    if (!mapRef?.current?.invalidateSize) return;
    mapRef.current.invalidateSize();
  }, [user.preferences.aspectRatioPage]);

  useEffect(() => {
    if (!wells.loading) {
      dispatch(listWells());
    }
  }, []);

  useEffect(() => {
    if ((USER_TYPE.PREMIUM_ADMIN === user.type || USER_TYPE.PREMIUM_REALTIME_MPD_ADMIN === user.type) || (user.type === USER_TYPE.PREMIUM_SELECT_ADMIN && !containsAnyKeys([USER_TYPE.PREMIUM_MPD_USER, USER_TYPE.PREMIUM_MPD_MANAGER], user.accessLevels))) {
      dispatch(listMPDCustomers());
    }
  }, []);

  useEffect(() => {
    if (!wells.loading && !rigs.loading) {
      const wellData = wells.data;
      wellData.forEach(well => {
        const rig = rigOption.find(rigData => rigData.WellID === well.wellInfo_Id);
        if (rig) {
          well.operator = well?.operator ?? rig.ENVOperator;
          well.rig = well?.rig ?? rig.RigName_Number;
        }
        well.wellsInfoId = well._id;
      });
      setWellsData(wellData);
    }
  }, [wells.data, wells.loading, rigs.loading]);

  useEffect(() => {
    if (!rigs.loading && rigs.data.length === 0) {
      dispatch({
        type: REDUX_ACTIONS.RIGS_LIST_LOADING,
      });
      dispatch(listRigs());
    }
  }, []);

  useEffect(() => {
    if (rigs?.data?.length) {
      const data = rigs.data.filter(record => record.LeaseName !== 'UNKNOWN' && record.WellNumber !== 'UNKNOWN');
      const uniqueRecords = [];
      const seenKeys = new Set();
      data.forEach(record => {
        const key = `${record.LeaseName}-${record.WellNumber}`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          uniqueRecords.push(record);
        }
      });
      setRigOption(uniqueRecords);
    }
  }, [rigs.data]);

  useEffect(() => {
    if (wells?.data[0]?.name === '') {
      const id = wells?.data[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 1
        });
        apiRef.current.setCellMode(id, 'name', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'name');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [wells.data.length]);

  const getPinColor = (status) => {
    return status ? 'green' : 'red';
  };

  const showSurveyDialog = (surveyData) => {
    setShowSurveyModal(true);
    setSurveyData(surveyData);
  };

  const showDrillStringDialog = (drillStringData) => {
    setShowDrillStringModal(true);
    setDrillStringData(drillStringData);
  };

  const showDrillFluidDialog = (drillFluidData) => {
    setShowDrillFluidModal(true);
    setDrillFluidData(drillFluidData);
  };
  const showGeometryDialog = (geometryData) => {
    setShowGeometryModal(true);
    setGeometryData(geometryData);
  };
  const showWindowDialog = (windowData) => {
    setShowWindowModal(true);
    setWindowData(windowData);
  };

  const getMapPin = (status) => {
    const pinColor = getPinColor(status);
    const pinIcon = pinColor ? pins[pinColor] : pins.default;
    return (
      pinIcon &&
      new Icon({
        iconUrl: pinIcon,
        iconSize: [15, 25],
        popupAnchor: [0, -5],
      })
    );
  };

  const getGoogleMapPin = (status) => {
    const pinColor = getPinColor(status);
    const pinIcon = pinColor ? pins[pinColor] : pins.default;
    return {
      url: pinIcon,
      // eslint-disable-next-line no-undef
      scaledSize: new google.maps.Size(15, 25),
      // eslint-disable-next-line no-undef
      size: new google.maps.Size(15, 25)
    };
  };

  const handleSave = (row) => {
    if (row?.isNewRow) {
      dispatch(createNewWell(row));
    } else {
      dispatch(updateWellDetails(row));
    }
  };

  const handleDelete = (row) => {
    if (row?.isNewRow) {
      dispatch(updateWells(row._id));
    } else {
      dispatch(deleteWell(row));
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const renderActions = ({
    row
  }) => {
    return (
      <Grid item container justifyContent="center" alignContent='center' alignItems='center'>
        <Box>
          <Tooltip title={TOOLTIPS.save}>
            <span>
              <IconButton
                disabled={!row?.name || !row?.latitude || !row?.longitude || wells.saveWellsDetails || !row?.rig || !row?.operator}
                variant="solid"
                onClick={() => {
                  handleSave(row);
                }}
              >
                <SaveRounded />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box>
          <Tooltip title={TOOLTIPS.delete}>
            <span>
              <IconButton
                variant="solid"
                onClick={() => {
                  setDeleteRow(row);
                  setDeleteAction(true);
                }}
              >
                <DeleteRounded />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box container justifyContent="center" sx={{ display: !isHydraulicsAdmin && 'none' }}>
          <Tooltip title={TOOLTIPS.viewPermissions}>
            <span>
              <IconButton
                variant="solid"
                key="permissions"
                sx={{ color: row?.permissionId && 'green' }}
                disabled={!row.wellsInfoId || row?.isNewRow}
                onClick={() => {
                  setPermissions({
                    open: true,
                    data: row
                  });
                }}
              >
                <PersonAddAlt />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box>
          <Tooltip title={TOOLTIPS.uploadJson}>
            <span>
              <IconButton
                disabled={row.isNewRow || !isHydraulicsAdmin}
                variant="solid"
                onClick={() => {
                  setImportRow(row);
                  setImportConfirmAction(true);
                }}
              >
                <FileUpload />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box>
          <Tooltip title={TOOLTIPS.importFromWell}>
            <span>
              <IconButton
                disabled={row.isNewRow || !isHydraulicsAdmin}
                variant="solid"
                onClick={() => {
                  setShowImportDialog({
                    open: true,
                    data: row
                  });
                }}
              >
                <CloudSyncRounded />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box>
          {wellsInputsDownload === row?.wellsInfoId
            ? (
            <Box container p={'12px'}>
              <CircularProgress size={18} />
            </Box>
              )
            : (
            <Tooltip title={TOOLTIPS.downloadWellsInputs}>
              <span>
                <IconButton
                  disabled={row.isNewRow || wellsInputsDownload || !isHydraulicsAdmin}
                  variant="solid"
                  onClick={() => {
                    setWellsInputsDownload(row.wellsInfoId);

                    const payload = {
                      fileName: `${row?.rig?.trim()}_${row.name?.trim()}_inputs`,
                      wellsInfoId: row.wellsInfoId
                    };
                    dispatch(downloadWellInputs(payload, () => {
                      setWellsInputsDownload(false);
                    }));
                  }}
                >
                  <FileDownload />
                </IconButton>
              </span>
            </Tooltip>
              )}
        </Box>
      </Grid>
    );
  };

  const updateData = (id, field, value) => {
    const { wells } = store.getState();
    if (value?.WellID !== '' && wells.data.some(well => well.wellInfo_Id === value.WellID) && field === 'name') {
      dispatch(
        enqueueSnackbar(
          'Please select different Well',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return;
    }

    if (value?.WellID === '' && wells.data.some(well => well?.name?.toLowerCase().trim() === value?.LeaseName?.toLowerCase().trim()) && field === 'name') {
      dispatch(
        enqueueSnackbar(
          'Well name already exists, Please enter a different Well name',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return;
    }
    const updatedProperties = wells.data.map(item => {
      if (item._id === id) {
        return {
          ...item,
          [field]: value?.LeaseName + ' ' + value.WellNumber,
          latitude: value?.RigLatitudeWGS84?.toString() ?? item?.latitude,
          longitude: value?.RigLongitudeWGS84?.toString() ?? item?.longitude,
          lastUpdate: new Date(item?.lastUpdate)?.toLocaleString() ?? new Date()?.toLocaleString(),
          wellInfo_Id: value?.WellID,
          jobId: item?.jobId || '',
          operator: value?.ENVOperator ?? item?.operator,
          rig: value?.RigName_Number ?? item?.rig
        };
      }
      return item;
    });
    setWellsData(updatedProperties);
    dispatch({
      type: REDUX_ACTIONS.WELLS_LIST_SUCCESS,
      payload: {
        data: updatedProperties,
        paginatedElements: updatedProperties.length,
      },
    });
  };

  const updateFieldData = (id, field, value) => {
    const { wells } = store.getState();
    const parsedValue = field === 'latitude' || field === 'longitude' ? parseFloat(value) : value;
    if ((field === 'latitude' || field === 'longitude') && isNaN(parsedValue)) {
      return;
    }
    const updatedProperties = wells.data.map(item => {
      if (item._id === id) {
        return {
          ...item,
          [field]: parsedValue
        };
      }
      return item;
    });
    setWellsData(updatedProperties);
    dispatch({
      type: REDUX_ACTIONS.WELLS_LIST_SUCCESS,
      payload: {
        data: updatedProperties,
        paginatedElements: updatedProperties.length,
      },
    });
  };

  const updateFavourite = (row, value) => {
    const { wells } = store.getState();
    const updatedProperties = wells.data.map(item => {
      if (item._id === row?._id) {
        return {
          ...item,
          isFavorite: value
        };
      }
      return item;
    });
    setWellsData(updatedProperties);
    dispatch({
      type: REDUX_ACTIONS.WELLS_LIST_SUCCESS,
      payload: {
        data: updatedProperties,
        paginatedElements: updatedProperties.length,
      },
    });
  };

  const updateMPDCustomer = (mpdCustomerData) => {
    dispatch(updateWellDetails(mpdCustomerData));
  };

  const showHydraulicsDialog = () => {
    setIsHydraulicsDialogOpen(true);
  };

  const renderWellsCustomersCell = (_props) => {
    const isLoading = wells.loading || rigs.loading || _props?.row?.isNewRow;
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          key="hydraulics"
          disabled={isLoading}
          onClick={() => {
            setShowMPDCustomer({
              open: true,
              data: _props.row
            });
          }}
        >
          <Handshake style={{ color: _props.row?.CompanyId?.length ? 'green' : '' }} />
        </IconButton>
      </Grid>
    );
  };

  const renderWellsFavoritesCell = (_props) => {
    const isLoading = wells.loading || rigs.loading;
    return (
      <Grid container justifyContent="center">
        {_props.row.isFavorite
          ? <IconButton
            variant="solid"
            key="hydraulics"
            disabled={isLoading}
            onClick={() => {
              if (_props?.row?.isNewRow) {
                updateFavourite(_props?.row, false);
              } else {
                const body = {
                  type: 'remove',
                  inputId: _props?.row?._id
                };
                dispatch(updateWellsFavorite(body));
              }
            }}
          >
            <Star />
          </IconButton>
          : <IconButton
            disabled={isLoading}
            variant="solid"
            key="hydraulics"
            onClick={() => {
              if (_props?.row?.isNewRow) {
                updateFavourite(_props?.row, true);
              } else {
                const body = {
                  type: 'add',
                  inputId: _props?.row?._id
                };
                dispatch(updateWellsFavorite(body));
              }
            }}
          >
            <StarBorder />
          </IconButton>
        }
      </Grid>
    );
  };

  const renderHydraulicCell = (_props) => {
    return (
      <Grid container justifyContent="center">
        {_props.row.hydraulicsInputs && <IconButton
          variant="solid"
          key="hydraulics"
          onClick={() => {
            const jobData = {
              _id: _props?.row?.jobId,
              fromWells: true,
              Rig: _props.row.rig,
              wellsInfoId: _props.row.wellsInfoId,
              Well: _props?.row?.name
            };
            setCurrentJob(jobData);
            setWellDetails(_props);
            showHydraulicsDialog();
          }}
        >
          <DirectionsRunRounded />
        </IconButton>}
      </Grid>
    );
  };

  const RenderInputColumn = (row) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    return (
      <>
        <Box
          component="span"
          className={wells?.loading ? classes.submitCellDisable : classes.submitCell}
          onClick={(e) => {
            if (!row?.isNewRow) {
              handleClick(e);
            }
          }}
        >
          <IconButton sx={{ p: 0 }}>
            <ArrowDropDownIcon sx={{ fontSize: '2rem' }} />
          </IconButton>
          Select
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <MenuItem
            disabled={
              wells?.loading ||
              row?.row?.isNewRow
            }
            onClick={() => {
              showSurveyDialog(row);
              handleClose();
            }}
          >
            <ListItemIcon>
            <FactCheckTwoTone fontSize="medium"/>
            </ListItemIcon>
            <ListItemText primary="Surveys"
              primaryTypographyProps={listItemTextPrimary}
            />
          </MenuItem>
          <MenuItem
            disabled={
              wells?.loading ||
              row?.row?.isNewRow
            }
            onClick={() => {
              showGeometryDialog(row);
              handleClose();
            }}
          >
            <ListItemIcon>
              <CalendarViewDay fontSize="medium" sx={{ transform: 'rotate(90deg)' }} />
            </ListItemIcon>
            <ListItemText primary="Geometry"
              primaryTypographyProps={listItemTextPrimary}
            />
          </MenuItem>
          <MenuItem
            disabled={
              wells?.loading ||
              row?.row?.isNewRow
            }
            onClick={() => {
              showDrillStringDialog(row);
              handleClose();
            }}
          >
            <ListItemIcon>
              <DragHandle fontSize="medium" sx={{ transform: 'rotate(90deg)' }} />
            </ListItemIcon>
            <ListItemText primary="Drillstring"
              primaryTypographyProps={listItemTextPrimary}
            />
          </MenuItem>
          <MenuItem
            disabled={
              wells?.loading ||
              row?.row?.isNewRow
            }
            onClick={() => {
              showDrillFluidDialog(row);
              handleClose();
            }}
          >
            <ListItemIcon>
              <Water fontSize="medium" />
            </ListItemIcon>
            <ListItemText primary="Drilling Fluid"
              primaryTypographyProps={listItemTextPrimary}
            />
          </MenuItem>
          <MenuItem
            disabled={
              wells?.loading ||
              row?.row?.isNewRow
            }
            onClick={() => {
              showWindowDialog(row);
              handleClose();
            }}
          >
            <ListItemIcon>
              <StackedLineChart fontSize="medium" sx={{ transform: 'rotate(90deg)' }} />
            </ListItemIcon>
            <ListItemText primary="Drilling Window"
              primaryTypographyProps={listItemTextPrimary}
            />
          </MenuItem>
          <MenuItem
            disabled={
              wells?.loading ||
              row?.row?.isNewRow
            }
            onClick={() => {
              setShowPWDModal({
                open: true,
                data: row
              });
              handleClose();
            }}
          >
            <ListItemIcon>
              <Sensors fontSize="medium" />
            </ListItemIcon>
            <ListItemText primary="PWD"
              primaryTypographyProps={listItemTextPrimary}
            />
          </MenuItem>
        </Menu>
      </>
    );
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField {...params} updateData={updateFieldData} />;
  };

  const renderValueNumberCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly={true} {...params} updateData={updateFieldData} />;
  };

  const renderWellsNameEditCell = (params) => {
    return <SelectAutoComplete closeCellAfterSelect={true} {...params} data={rigOption} updateData={updateData} />;
  };

  const renderRigNameEditCell = (params) => {
    return <SelectAutoComplete type='rig' closeCellAfterSelect={true} {...params} data={rigOption} updateData={updateData} />;
  };

  const [columnsList, setColumnsList] = useState(columns(renderActions, renderWellsNameEditCell, renderValueCell, renderValueNumberCell, renderRigNameEditCell, renderHydraulicCell, renderWellsFavoritesCell, renderWellsCustomersCell, user, RenderInputColumn));

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  const handleVisibiltyModel = (newModel) => {
    setColumnVisibilityModel(newModel);
  };

  useEffect(() => {
    if (!wells.loading) {
      const accessList = [USER_TYPE.PREMIUM_ADMIN, USER_TYPE.PREMIUM_REALTIME_MPD_ADMIN, USER_TYPE.PREMIUM_SELECT_ADMIN];
      let columnsList = columns(renderActions, renderWellsNameEditCell, renderValueCell, renderValueNumberCell, renderRigNameEditCell, renderHydraulicCell, renderWellsFavoritesCell, renderWellsCustomersCell, user, RenderInputColumn);
      if ((user.type === USER_TYPE.PREMIUM_SELECT_ADMIN && containsAnyKeys([USER_TYPE.PREMIUM_MPD_USER, USER_TYPE.PREMIUM_MPD_MANAGER], user.accessLevels))) {
        const filterTerms = ['customers'];
        columnsList = columnsList.filter(obj => !filterTerms.includes(obj.field));
      }
      if (!accessList.includes(user.type)) {
        const filterTerms = ['customers'];
        columnsList = columnsList.filter(obj => !filterTerms.includes(obj.field));
      }
      setColumnsList(columnsList);
    }
  }, [wells.loading, rigOption, wellsInputsDownload]);

  return (
    <Grid container justifyContent="center" sx={{ height: 'calc(100vh - 80px)', flexDirection: 'column' }}>
      <Grid item>
        <Map
          mapType={mapType}
          data={wells}
          mapData={wells.marker_data}
          markerData={wells.marker_data}
          latitudeField={'latitude'}
          longitudeField={'longitude'}
          mapPinField1={'hydraulicsInputs'}
          popUpLabel={['name']}
          getGoogleMapPin={getGoogleMapPin}
          getMapPin={getMapPin}
          gmapRowId={gmapRowId}
          rowId={rowId}
          excludePopupLabel
          resizable={true}
        />
      </Grid>
      <Grid item sx={{ flex: 1, minHeight: 0 }}>
        {showSurveyModal && <SurveyModal
          onCloseClick={() => setShowSurveyModal(false)}
          isOpen={showSurveyModal}
          surveyData={surveyData}
        />
        }
        {
          showDrillFluidModal && <DrillFluidModal
            onCloseClick={() => setShowDrillFluidModal(false)}
            isOpen={showDrillFluidModal}
            drillFluidData={drillFluidData}
          />
        }
        {showDrillStringModal && <DrillStringModal
          onCloseClick={() => setShowDrillStringModal(false)}
          isOpen={showDrillStringModal}
          drillStringData={drillStringData}
        />}
        {showGeometryModal && <GeometryModal
          onCloseClick={() => setShowGeometryModal(false)}
          isOpen={showGeometryModal}
          geometryData={geometryData}
        />
        }
        {showWindowModal && <WindowModal
          onCloseClick={() => setShowWindowModal(false)}
          isOpen={showWindowModal}
          windowData={windowData}
        />
        }
        {isHydraulicsDialogOpen && <HydraulicsModal
          isOpen={isHydraulicsDialogOpen}
          onCloseClick={() => setIsHydraulicsDialogOpen(false)}
          selectedJob={currentJob}
          wellDetails={wellDetails}
        />
        }
        {
          deleteAction && <DeleteConfirmationModal
            open={deleteAction}
            deleteRow={deleteRow}
            handleDelete={handleDelete}
            handleClose={setDeleteAction}
          />
        }
        {
          importConfirmAction && <WellsImportConfirmationModal
            open={importConfirmAction}
            handleClose={setImportConfirmAction}
            importRow={importRow}
          />
        }
        {showMPDCustomer.open && <CustomerDialog
          handleSave={updateMPDCustomer}
          open={showMPDCustomer.open}
          handleClose={() => {
            setShowMPDCustomer({
              open: false,
              data: {}
            });
          }}
          rowData={showMPDCustomer.data}
        />
        }
        {showPWDModal.open && <PWDModal
          onCloseClick={() => setShowPWDModal({
            open: false,
            data: null
          })}
          isOpen={showPWDModal.open}
          wellsData={showPWDModal.data}
        />
        }
        {
          showImportDialog.open && <WellsImportDialog rowData={showImportDialog.data} isOpen={showImportDialog.open} onCloseClick={() => {
            setShowImportDialog({
              open: false,
              data: null
            });
          }} />
        }
        <Datagrid
          apiRef={apiRef}
          data={wellsData}
          getRowId={(row) => row._id}
          columns={columnsList}
          loading={wells.loading || rigs.loading || mpdCustomer?.loading}
          sx={{
            '.MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold !important',
            },
            '.MuiDataGrid-cell': {
              fontSize: 'medium',
              fontWeight: 'bold !important'
            },
          }}
          onRowClick={({ row: well }) => {
            if (mapType === MAP_TYPES.GOOGLEMAP) {
              setGmapRowId(well._id);
            } else {
              setRowId(well);
            }
          }}
          onRowDoubleClick={() => {
          }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) => {
            handleVisibiltyModel(newModel);
          }}
          toolbar={{
            options: {
              columns: isHydraulicsAdmin,
              filters: true,
              export: false,
            },
          }}
          onFilterModelChange={() => {
            setTimeout(() => {
              const filterData = gridFilteredSortedRowEntriesSelector(apiRef.current.state, apiRef.current.instanceId).map(item => {
                return { ...item.model };
              });
              if (filterData) {
                dispatch({
                  type: REDUX_ACTIONS.WELLS_MARKER_FILTER,
                  payload: {
                    data: filterData,
                  }
                });
              }
            }, 300);
          }}
          onCellEditCommit={({ id, value, field }) => {
            updateData(id, field, value);
          }}
          editMode="cell"
          pageSize={50}
          autoHeight={false}
          height='100%'
        />
        {permissions?.open
          ? (
            <PermissionsModal
              isOpen={permissions?.open}
              data={
                {
                  ...permissions?.data,
                  collection: PERMISSION_DIALOG_SOURCES?.wellsInfos,
                }
              }
              onCloseClick={() => {
                setPermissions({
                  open: false,
                  data: {}
                });
              }}
              refreshParentDialog={() => {
                dispatch(listWells());
              }}
            />
            )
          : null}
      </Grid>
    </Grid>
  );
};
Wells.propTypes = {
  row: PropTypes.object,
};
export default Wells;
